// Handles EmptyState and ErrorState
import * as React from 'react';
import { instructionHooks } from '../network/endpoints.js';
import type { InstructionListItem, InstructionStatus } from '../types.js';
import { MarkSentModal } from './MarkSentModal.js';
import { Money } from '../../../components/ui/Money.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale.js';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';

type PendingAction = { type: 'approve' | 'cancel' | 'retry'; instructionId: string };

const CONFIRM_COPY: Record<PendingAction['type'], { title: string; description: string; confirmLabel: string }> = {
  approve: {
    title: 'Approve instruction',
    description: 'Approve this instruction for processing?',
    confirmLabel: 'Approve',
  },
  cancel: {
    title: 'Cancel instruction',
    description: 'Cancel this instruction? This cannot be undone.',
    confirmLabel: 'Cancel Instruction',
  },
  retry: {
    title: 'Retry instruction',
    description: 'This creates a new instruction with the same details. Proceed?',
    confirmLabel: 'Retry',
  },
};

export function InstructionQueue() {
  const [activeTab, setActiveTab] = React.useState<InstructionStatus>('PENDING');
  const [selectedInstruction, setSelectedInstruction] = React.useState<string | null>(null);
  const [pendingAction, setPendingAction] = React.useState<PendingAction | null>(null);

  const { data, isLoading } = instructionHooks.useList({ status: activeTab });

  const cancelMutation = instructionHooks.useCancel();
  const retryMutation = instructionHooks.useRetry();
  const approveInstructionMutation = instructionHooks.useApproveInstruction();

  const handleConfirmPendingAction = () => {
    if (!pendingAction) return;
    const { type, instructionId } = pendingAction;
    if (type === 'approve') approveInstructionMutation.mutate(instructionId);
    if (type === 'cancel') cancelMutation.mutate(instructionId);
    if (type === 'retry') retryMutation.mutate(instructionId);
    setPendingAction(null);
  };

  if (isLoading) {
    return <div>Loading instructions...</div>;
  }

  const instructions = data?.data || [];

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader title="Instruction Queue" />
      <div className="flex-1 w-full min-h-0">
      <div className="flex border-b mb-6">
        {(['PENDING', 'APPROVED', 'SENT_TO_RAIL', 'RECONCILED', 'FAILED'] as InstructionStatus[]).map(tab => (
          <Button
            key={tab}
            variant="ghost"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium text-sm rounded-none bg-transparent hover:bg-transparent ${activeTab === tab ? 'border-b-2 border-accent-500 text-accent-600' : 'text-text-secondary hover:text-text-primary'}`}
          >
            {tab.replace(/_/g, ' ')}
          </Button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-2">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Beneficiary</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Reference</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-surface-1 divide-y divide-border">
            {instructions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-text-muted">No instructions found.</td>
              </tr>
            ) : (
              instructions.map((item: InstructionListItem) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">{item.instructionType}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">{item.beneficiaryName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    <Money paise={item.amount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{item.referenceType} ({item.referenceId})</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{new Date(item.createdAt).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {activeTab === 'PENDING' && (
                      <Button
                        variant="ghost"
                        onClick={() => setPendingAction({ type: 'approve', instructionId: item.id })}
                        className="text-accent-600 hover:text-accent-700 mr-4"
                      >
                        Approve
                      </Button>
                    )}
                    {activeTab === 'APPROVED' && (
                      <Button 
                        variant="ghost"
                        onClick={() => setSelectedInstruction(item.id)}
                        className="text-success-500 hover:text-success-text mr-4"
                      >
                        Mark as sent
                      </Button>
                    )}
                    {(activeTab === 'PENDING' || activeTab === 'APPROVED') && (
                      <Button
                        variant="ghost"
                        onClick={() => setPendingAction({ type: 'cancel', instructionId: item.id })}
                        className="text-danger-500 hover:text-danger-text"
                      >
                        Cancel
                      </Button>
                    )}
                    {activeTab === 'FAILED' && (
                      <Button
                        variant="ghost"
                        onClick={() => setPendingAction({ type: 'retry', instructionId: item.id })}
                        className="text-accent-600 hover:text-accent-700"
                      >
                        Retry
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <MarkSentModal
        isOpen={!!selectedInstruction}
        instructionId={selectedInstruction!}
        onClose={() => setSelectedInstruction(null)}
      />

      <ConfirmDialog
        open={!!pendingAction}
        title={pendingAction ? CONFIRM_COPY[pendingAction.type].title : ''}
        description={pendingAction ? CONFIRM_COPY[pendingAction.type].description : ''}
        confirmLabel={pendingAction ? CONFIRM_COPY[pendingAction.type].confirmLabel : 'Confirm'}
        loading={approveInstructionMutation.isPending || cancelMutation.isPending || retryMutation.isPending}
        onConfirm={handleConfirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />
      </div>
    </div>
  );
}
